import { AxiosResponse } from 'axios';
import { api } from '../axios';
import {
  WarehouseReceipt,
  WarehouseReceiptLine,
  CreateWarehouseReceiptRequest,
  UpdateWarehouseReceiptRequest,
  UpdateWarehouseReceiptLineRequest,
  WarehouseReceiptListQuery,
  WarehouseReceiptLineListQuery,
  PaginatedWarehouseReceiptResponse,
  PaginatedWarehouseReceiptLineResponse,
  WarehouseReceiptStatusCounts,
} from '@/types/warehouseReceipt';

export interface ApiResponse<T> {
  data: T;
  message: string;
  timestamp: string;
}

const warehouseCommandHeaders = () => {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const operationID = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return { 'Idempotency-Key': operationID };
};

export const warehouseReceiptApi = {
  // Warehouse Receipt APIs
  getAll: async (query?: WarehouseReceiptListQuery): Promise<PaginatedWarehouseReceiptResponse> => {
    const response: AxiosResponse<ApiResponse<PaginatedWarehouseReceiptResponse>> = await api.get('/warehouse-receipts', {
      params: query,
    });
    return response.data.data;
  },

  getByCode: async (code: string): Promise<WarehouseReceipt> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceipt>> = await api.get(`/warehouse-receipts/${code}`);
    return response.data.data;
  },

  create: async (data: CreateWarehouseReceiptRequest): Promise<WarehouseReceipt> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceipt>> = await api.post('/warehouse-receipts', data, {
      headers: warehouseCommandHeaders(),
    });
    return response.data.data;
  },

  update: async (code: string, data: UpdateWarehouseReceiptRequest): Promise<WarehouseReceipt> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceipt>> = await api.put(`/warehouse-receipts/${code}`, data, {
      headers: warehouseCommandHeaders(),
    });
    return response.data.data;
  },

  delete: async (code: string): Promise<void> => {
    await api.delete(`/warehouse-receipts/${code}`, { headers: warehouseCommandHeaders() });
  },

  confirm: async (code: string): Promise<WarehouseReceipt> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceipt>> = await api.put(`/warehouse-receipts/${code}/confirm`, undefined, {
      headers: warehouseCommandHeaders(),
    });
    return response.data.data;
  },

  receive: async (code: string): Promise<WarehouseReceipt> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceipt>> = await api.put(`/warehouse-receipts/${code}/receive`, undefined, {
      headers: warehouseCommandHeaders(),
    });
    return response.data.data;
  },

  cancel: async (code: string): Promise<WarehouseReceipt> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceipt>> = await api.put(`/warehouse-receipts/${code}/cancel`, undefined, {
      headers: warehouseCommandHeaders(),
    });
    return response.data.data;
  },

  getLines: async (code: string): Promise<WarehouseReceiptLine[]> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceiptLine[]>> = await api.get(`/warehouse-receipts/${code}/lines`);
    return response.data.data;
  },

  getStatusCounts: async (query?: Omit<WarehouseReceiptListQuery, 'status' | 'page' | 'page_size' | 'sort_by' | 'sort_order'>): Promise<WarehouseReceiptStatusCounts> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceiptStatusCounts>> = await api.get('/warehouse-receipts/status-counts', {
      params: query,
    });
    return response.data.data;
  },


};

export const warehouseReceiptLineApi = {
  getAll: async (query?: WarehouseReceiptLineListQuery): Promise<PaginatedWarehouseReceiptLineResponse> => {
    const response: AxiosResponse<ApiResponse<PaginatedWarehouseReceiptLineResponse>> = await api.get('/warehouse-receipt-lines', {
      params: query,
    });
    return response.data.data;
  },

  getByCode: async (code: string): Promise<WarehouseReceiptLine> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceiptLine>> = await api.get(`/warehouse-receipt-lines/${code}`);
    return response.data.data;
  },

  update: async (code: string, data: UpdateWarehouseReceiptLineRequest): Promise<WarehouseReceiptLine> => {
    const response: AxiosResponse<ApiResponse<WarehouseReceiptLine>> = await api.put(`/warehouse-receipt-lines/${code}`, data, {
      headers: warehouseCommandHeaders(),
    });
    return response.data.data;
  },

  delete: async (code: string): Promise<void> => {
    await api.delete(`/warehouse-receipt-lines/${code}`, { headers: warehouseCommandHeaders() });
  },
};
